'use client'

import { get, useCallbackRef } from '@vtex/admin-ui'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react'
import { IntlShape, useIntl } from 'react-intl'
import type { DefaultProps } from 'typings'
import { Suite, create } from 'vest'

export type ValidationFn<TValue = any, TData = any> = (
  value: TValue,
  context: { data: TData; intl: IntlShape; [key: string]: any }
) => void

export type ValidationConfig<T = any, D = any> = {
  fieldName: string | string[]
  validateFn: ValidationFn<T, D>
}

export type ValidationConfigGetter<T = any, D = any> = () => ValidationConfig<
  T,
  D
>

type ValidationSuite = Suite<string, string>

interface FormValidationContextValue {
  register: (id: string, configCb: () => ValidationConfig) => void
  unregister: (id: string) => void
  suite: () => ValidationSuite
}

export const FormValidationContext = createContext<
  FormValidationContextValue | undefined
>(undefined)

export function useFormValidation<TValue = any, TData = any>(
  id: string,
  validationConfig?: ValidationConfig
) {
  const context = useContext(FormValidationContext)

  if (context === undefined) {
    throw new Error(
      'FormValidationContext must be defined in order to use useFormValidation hook'
    )
  }

  const ref = useCallbackRef(() => validationConfig, [validationConfig])

  useEffect(() => {
    if (validationConfig === undefined) return

    context.register(id, ref as ValidationConfigGetter<TValue, TData>)

    return () => context.unregister(id)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { suite: context.suite }
}

export function FormValidationContextProvider(props: DefaultProps) {
  const intl = useIntl()
  const configFns = useRef<Map<string, ValidationConfigGetter>>(new Map())

  function register(id: string, configFn: ValidationConfigGetter) {
    if (configFns.current) configFns.current.set(id, configFn)
  }

  function unregister(id: string) {
    configFns.current.delete(id)
  }

  function buildTestsSuite() {
    return Array.from(configFns.current.values()).flatMap(
      (getValidationConfig) => getValidationConfig()
    )
  }

  const suite = useCallback(
    () =>
      create((data) => {
        buildTestsSuite().forEach(({ fieldName, validateFn }) => {
          const values = Array.isArray(fieldName)
            ? fieldName.map((name) => get(data, name))
            : get(data, fieldName)

          validateFn(values, { data, intl })
        })
      }),
    [intl]
  )

  return (
    <FormValidationContext.Provider value={{ register, unregister, suite }}>
      {props.children}
    </FormValidationContext.Provider>
  )
}
